import React, { useState, useRef, useEffect } from 'react';

import { Button, Icon, Text, View } from 'native-base';
import { ActivityIndicator } from 'react-native';
import EStyleSheet from 'react-native-extended-stylesheet';
import FlashMessage from 'react-native-flash-message';
import moment from 'moment';
import {
  Entypo,
  FontAwesome,
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
} from '@expo/vector-icons';
import { FooterTab, Footer } from '../bbot-component-library';
import Colors from '../constants/Colors';
import RefundReasonModal from './RefundReasonModal';
import SnoozeOrderModal from './KDS/SnoozeOrderModal';
import { SendReceiptModal } from './index';
import DriverModal from './DriverModal';
import EditOrderModal from './Modals/EditOrderModal';
import RejectCateringDraftOrderModal from './Modals/RejectCateringDraftOrderModal';
import OrderHelper, { getCateringDraftOrderStatus } from '../helpers/OrderHelper';
import { confirmAction } from '../helpers/confirmAction';
import { dispatchOrderEdit } from '../helpers/dispatchOrderEdit';
import { useModalState } from '../helpers/useModalState';
import API from '../api';

const ACTION_PRINT = 'ACTION_PRINT';
const ACTION_SMS = 'ACTION_SMS';
const ACTION_UNSNOOZE = 'ACTION_UNSNOOZE';
const ACTION_ACCEPT_DRAFT = 'ACTION_ACCEPT_DRAFT';
const ACTION_REJECT_DRAFT = 'ACTION_REJECT_DRAFT';
const ACTION_FINALIZE = 'ACTION_FINALIZE';

const MODAL_REFUND = 'MODAL_REFUND';
const MODAL_SNOOZE = 'MODAL_SNOOZE';
const MODAL_RECEIPT = 'MODAL_RECEIPT';
const MODAL_DRIVER = 'MODAL_DRIVER';
const MODAL_EDIT = 'MODAL_EDIT';
const MODAL_REJECT_DRAFT = 'MODAL_REJECT_DRAFT';

function OrderActionsMenu({
  order,
  selectedItems,
  setSelectItems,
  onDismiss,
  dir = 'horizontal',
}) {
  const { activeModal, openModal, closeModal: closeActiveModal, modalProps } = useModalState();

  const [processingAction, setProcessingAction] = useState(null);

  useEffect(() => {
    closeActiveModal();
    setProcessingAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);
  const flash = useRef(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const showFlash = (type, message) => {
    if (flash.current) {
      flash.current.showMessage({ position: 'top', floating: true, type, message });
    }
  };

  // Show success/danger flash based on an API result
  const showResultFlash = (result, successMsg, errorMsg) => {
    showFlash(
      result?.success ? 'success' : 'danger',
      result?.success ? successMsg : String(result?.error || errorMsg),
    );
  };

  const refundSuccess = () => {
    closeActiveModal();
    setSelectItems([]);
    showFlash('success', 'Refund completed successfully');
  };

  const printTicket = async () => {
    try {
      const res = await OrderHelper.printTicket(order, false);
      if (res) showFlash('success', 'Ticket sent to printer');
    } catch (e) {
      showFlash('danger', 'Error printing ticket');
    }
  };

  const printReceipt = async () => {
    setProcessingAction(ACTION_PRINT);
    try {
      let response;
      const { has_printers } = API.menuData.customer;
      if (has_printers && !API.config.force_pdf_printing) {
        response = await API.printTicket(order, true, true);
      } else {
        response = await API.printReceiptLocal(order);
      }
      if (!response.errorCode && API.menuData.customer.has_printers) {
        showFlash('success', 'Receipt sent to printer');
      }
      return response;
    } catch (e) {
      showFlash('danger', 'Error printing receipt');
    } finally {
      setProcessingAction(null);
    }
  };

  const resendText = () =>
    runAsyncAction(
      ACTION_SMS,
      () => API.textPatronAgain(order),
      null,
      'Error re-sending text',
      {
        onResult: (result) =>
          showResultFlash(result, 'Text notification has been re-sent to Patron', result?.warning || 'Error re-sending text'),
      },
    );

  const unSnooze = () =>
    runAsyncAction(
      ACTION_UNSNOOZE,
      () => API.snoozeOrder(order, null),
      null,
      'Error un-snoozing order',
      { onResult: () => onDismiss() },
    );

  const editOrder = async fieldsToEdit => {
    const errorMsg = 'Error: changes have not been saved';
    try {
      const result = await dispatchOrderEdit(order, fieldsToEdit);
      if (result) {
        showResultFlash(result, 'Changes saved', errorMsg);
      } else {
        showFlash('info', 'No changes to save');
      }
    } catch (e) {
      showFlash('danger', errorMsg);
    } finally {
      if (isMountedRef.current) closeActiveModal();
    }
  };

  // Shared helper for async actions that follow the processingAction pattern.
  // Manages processingAction state and isMountedRef guards around the API call.
  // Pass onResult callback to override the default showResultFlash behavior.
  const runAsyncAction = async (actionKey, apiFn, successMsg, errorMsg, { onResult } = {}) => {
    setProcessingAction(actionKey);
    try {
      const result = await apiFn();
      if (!isMountedRef.current) return;
      if (onResult) {
        onResult(result);
      } else {
        showResultFlash(result, successMsg, errorMsg);
      }
    } catch (e) {
      if (isMountedRef.current) showFlash('danger', errorMsg);
    } finally {
      if (isMountedRef.current) setProcessingAction(null);
    }
  };

  const acceptCateringDraftOrder = async () => {
    const confirmed = await confirmAction(
      'Accept Catering Draft Order',
      'Please verify all items are available before accepting. This confirms the order for the customer.',
    );
    if (!confirmed || !isMountedRef.current) return;
    await runAsyncAction(
      ACTION_ACCEPT_DRAFT,
      () => API.acceptCateringDraftOrder(order),
      'Catering draft order accepted',
      'Error accepting catering draft order',
    );
  };

  const handleRejectConfirm = async reason => {
    closeActiveModal();
    await runAsyncAction(
      ACTION_REJECT_DRAFT,
      () => API.rejectCateringDraftOrder(order, reason),
      'Catering draft order rejected',
      'Error rejecting catering draft order',
    );
  };

  const finalizeCateringDraftOrderNow = async () => {
    const confirmed = await confirmAction(
      'Finalize Catering Draft Order',
      'This will process payment and send the order to the kitchen immediately. Continue?',
    );
    if (!confirmed || !isMountedRef.current) return;
    await runAsyncAction(
      ACTION_FINALIZE,
      () => API.finalizeCateringDraftOrder(order),
      'Catering draft order finalized and sent to kitchen',
      'Error finalizing catering draft order',
    );
  };

  const {
    isCateringDraft: isCateringDraftOrder,
    isPending: isPendingCateringDraftOrder,
    isAccepted: isAcceptedCateringDraftOrder,
  } = getCateringDraftOrderStatus(order);
  const cateringActionInProgress = [
    ACTION_ACCEPT_DRAFT,
    ACTION_REJECT_DRAFT,
    ACTION_FINALIZE,
  ].includes(processingAction);

  return (
    <View>
      <Footer style={{ backgroundColor: Colors.primary }}>
        <SideMenuButton
          icon="cash-refund"
          iconType={MaterialCommunityIcons}
          label="Refund"
          shown={!isCateringDraftOrder}
          onPress={() => openModal(MODAL_REFUND)}
        />
        <SideMenuButton
          icon="ticket"
          iconType={Entypo}
          label="Re-Print Ticket"
          shown={!isCateringDraftOrder}
          onPress={printTicket}
        />
        <SideMenuButton
          icon="receipt"
          iconType={MaterialIcons}
          label="Print Receipt"
          shown={!isCateringDraftOrder}
          onPress={printReceipt}
          disabled={processingAction === ACTION_PRINT}
          processing={processingAction === ACTION_PRINT}
        />
        <SideMenuButton
          icon="send"
          iconType={FontAwesome}
          label="Send Receipt"
          shown={!isCateringDraftOrder}
          onPress={() => openModal(MODAL_RECEIPT)}
        />
        <SideMenuButton
          icon="sleep-off"
          iconType={MaterialCommunityIcons}
          label="Un-Snooze"
          onPress={unSnooze}
          processing={processingAction === ACTION_UNSNOOZE}
          shown={order.is_snoozed}
        />
        <SideMenuButton
          icon="sleep"
          iconType={MaterialCommunityIcons}
          label="Snooze"
          shown={!isCateringDraftOrder && order.user_desired_time > moment()}
          onPress={() => openModal(MODAL_SNOOZE)}
        />
        <SideMenuButton
          icon="sms"
          iconType={MaterialIcons}
          label="Re-Send SMS"
          shown={order.kds_closed}
          onPress={resendText}
          processing={processingAction === ACTION_SMS}
        />
        <SideMenuButton
          icon="car"
          iconType={FontAwesome}
          label="Manage Driver"
          shown={!order.time_closed && !!order.driver_delivery_jobs.length}
          onPress={() => {
            openModal(MODAL_DRIVER);
          }}
        />
        <SideMenuButton
          icon="edit"
          iconType={MaterialIcons}
          label="Edit"
          shown={
            !isCateringDraftOrder || isPendingCateringDraftOrder || isAcceptedCateringDraftOrder
          }
          onPress={() => openModal(MODAL_EDIT)}
          disabled={isCateringDraftOrder && cateringActionInProgress}
        />
        <SideMenuButton
          icon="check-circle"
          iconType={MaterialIcons}
          label="Accept Draft Order"
          shown={isPendingCateringDraftOrder}
          onPress={acceptCateringDraftOrder}
          processing={processingAction === ACTION_ACCEPT_DRAFT}
          disabled={cateringActionInProgress || activeModal === MODAL_REJECT_DRAFT}
        />
        <SideMenuButton
          icon="cancel"
          iconType={MaterialIcons}
          label="Reject Draft Order"
          shown={isPendingCateringDraftOrder || isAcceptedCateringDraftOrder}
          onPress={() => openModal(MODAL_REJECT_DRAFT)}
          processing={processingAction === ACTION_REJECT_DRAFT}
          disabled={cateringActionInProgress}
        />
        <SideMenuButton
          icon="check-all"
          iconType={MaterialCommunityIcons}
          label="Finalize Now"
          shown={isAcceptedCateringDraftOrder}
          onPress={finalizeCateringDraftOrderNow}
          processing={processingAction === ACTION_FINALIZE}
          disabled={cateringActionInProgress}
        />
      </Footer>

      {isAcceptedCateringDraftOrder && order.lock_date && (
        <Text style={styles.lockDateInfo}>
          Auto-finalizes: {order.lock_date.format('MMM D, YYYY h:mm A')}
        </Text>
      )}

      <RefundReasonModal
        order={order}
        {...modalProps(MODAL_REFUND)}
        items={order.items.filter(i => i.status !== 'refunded')}
        selectedItems={selectedItems.map(i => i.orderitemid)}
        onSuccess={refundSuccess}
      />

      {!!order.user_desired_time && (
        <SnoozeOrderModal
          {...modalProps(MODAL_SNOOZE)}
          order={order}
          onSuccess={onDismiss}
        />
      )}

      <SendReceiptModal
        {...modalProps(MODAL_RECEIPT)}
        order={order}
        onSave={() => {
          closeActiveModal();
          showFlash('success', 'Receipt sent!');
        }}
      />

      <DriverModal
        visible={modalProps(MODAL_DRIVER).visible}
        order={order}
        onHide={closeActiveModal}
      />

      <EditOrderModal
        {...modalProps(MODAL_EDIT)}
        order={order}
        onSave={editOrder}
      />

      <RejectCateringDraftOrderModal
        {...modalProps(MODAL_REJECT_DRAFT)}
        onConfirm={handleRejectConfirm}
      />

      <FlashMessage ref={flash} />
    </View>
  );
}

export default OrderActionsMenu;

function SideMenuButton({
  onPress,
  icon,
  iconType = Ionicons,
  label,
  processing,
  disabled,
  shown = true,
}) {
  if (!shown) return null;
  return (
    <FooterTab>
      <Button
        vertical
        onPress={onPress}
        disabled={processing || disabled}
        style={sideMenuStyles.button}
      >
        <View style={sideMenuStyles.iconContainer}>
          {processing ? (
            <ActivityIndicator color={Colors.primary} />
          ) : (
            <Icon
              name={icon}
              as={iconType}
              size={6}
              style={sideMenuStyles.icon}
            />
          )}
        </View>
        <Text
          style={sideMenuStyles.label}
          numberOfLines={2}
        >
          {label}
        </Text>
      </Button>
    </FooterTab>
  );
}

const styles = EStyleSheet.create({
  lockDateInfo: {
    textAlign: 'center',
    fontSize: 12,
    color: Colors.darkGray,
    paddingVertical: 8,
  },
});

const sideMenuStyles = EStyleSheet.create({
  button: { alignItems: 'center' },
  iconContainer: { alignItems: 'center' },
  icon: { color: '#C0C6E7' },
  label: { paddingLeft: 5, paddingRight: 5, textAlign: 'center', color: '#C0C6E7' },
});
